import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Clock, 
  MapPin, 
  Briefcase, 
  RefreshCw, 
  LogOut,
  User,
  Shield,
  Eye,
  Coffee
} from 'lucide-react';
import { apiService } from '../services/api';
import { Team, User as UserType, WorkEntry, Project, Location } from '../types';
import WorkConfirmationDialog from './WorkConfirmationDialog';
import OnlineStatusService from '../services/onlineStatusService';
import { getCurrentTimestamp, getCurrentDateString } from '../utils/timeUtils';
import { useRealTime } from '../contexts/RealTimeContext';
import { isInAutomaticBreakTime, shouldUserBeOnAutomaticBreak } from '../utils/breakUtils';
import { useAutomaticBreak, automaticBreakService } from '../services/automaticBreakService';
import { useServerTime } from '../services/timeService';
import { isWorkLockedForUser, getWorkLockMessage } from '../utils/workLockUtils';

interface TabletDashboardProps {
  user: UserType;
  selectedTeams?: Team[];
  showAllWorkers?: boolean;
  onLogout: () => void;
}

interface WorkerStatusData {
  user: UserType;
  activeWorkEntry: WorkEntry | null;
  isWorking: boolean;
  isOnline: boolean;
  isOnBreak?: boolean;
  currentProject?: string;
  currentLocation?: string;
  startTime?: string;
}

// Worker card component to avoid duplication
const WorkerCard: React.FC<{ 
  workerStatus: WorkerStatusData;
  handleActivateWorker: (worker: UserType) => void;
  handleWorkAction: (worker: UserType, action: 'start' | 'stop') => void;
  realTimeConnected: boolean;
  getRoleIcon: (role: string) => React.ReactNode;
  getRoleColor: (role: string) => string;
  formatWorkTime: (timeString?: string) => string;
  formatDuration: (startTime?: string) => string;
}> = ({ 
  workerStatus, 
  handleActivateWorker, 
  handleWorkAction, 
  realTimeConnected,
  getRoleIcon,
  getRoleColor,
  formatWorkTime,
  formatDuration 
}) => (
  <div
    className={`border rounded-lg p-4 ${
      !workerStatus.isOnline
        ? 'border-gray-300 bg-gray-100 opacity-75'
        : workerStatus.isWorking 
        ? 'border-green-200 bg-green-50' 
        : 'border-gray-200 bg-white'
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {getRoleIcon(workerStatus.user.role)}
          <span className="font-semibold">
            {workerStatus.user.name}
          </span>
          <Badge 
            variant="outline" 
            className={getRoleColor(workerStatus.user.role)}
          >
            {workerStatus.user.role}
          </Badge>
        </div>
        {!workerStatus.isOnline && (
          <Badge className="bg-gray-600 text-white">
            Offline
          </Badge>
        )}
        {workerStatus.isOnline && workerStatus.isWorking && !workerStatus.isOnBreak && (
          <Badge className="bg-green-600 text-white">
            La Lucru
          </Badge>
        )}
        {workerStatus.isOnBreak && (
          <Badge className="bg-orange-600 text-white">
            Pauza automată
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-4">
        {workerStatus.isWorking ? (
          <div className="text-right text-sm">
            <div className="flex items-center gap-1 text-gray-600">
              <Briefcase className="w-3 h-3" />
              {workerStatus.currentProject}
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <MapPin className="w-3 h-3" />
              {workerStatus.currentLocation}
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <Clock className="w-3 h-3" />
              {formatWorkTime(workerStatus.startTime)} ({formatDuration(workerStatus.startTime)})
            </div>
          </div>
        ) : (
          <span className="text-gray-500 text-sm">
            Nu lucrează
          </span>
        )}
        {!workerStatus.isOnline ? (
          <Button
            onClick={() => handleActivateWorker(workerStatus.user)}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!realTimeConnected}
            title={!realTimeConnected ? 'Așteptați conectarea la server...' : ''}
          >
            {!realTimeConnected ? 'Conectare...' : 'Începe Lucru'}
          </Button>
        ) : (
          <Button
            onClick={() => handleWorkAction(
              workerStatus.user, 
              workerStatus.isWorking ? 'stop' : 'start'
            )}
            className={
              workerStatus.isWorking
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            }
            disabled={!realTimeConnected}
            title={!realTimeConnected ? 'Așteptați conectarea la server...' : ''}
          >
            {!realTimeConnected ? 'Conectare...' : (workerStatus.isWorking ? 'Oprește Lucrul' : 'Am Venit la Lucru')}
          </Button>
        )}
      </div>
    </div>
  </div>
);

const TabletDashboard: React.FC<TabletDashboardProps> = ({ 
  user, 
  selectedTeams,
  showAllWorkers = false, 
  onLogout 
}) => {
  const [workersStatus, setWorkersStatus] = useState<WorkerStatusData[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    user: UserType | null;
    action: 'start' | 'stop' | 'activate';
  }>({
    isOpen: false,
    user: null,
    action: 'start'
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  
  // Add real-time context
  const realTime = useRealTime();
  
  // Automatic break service
  const { status: automaticBreakStatus, forceCheck: forceBreakCheck } = useAutomaticBreak();
  
  // Server time synchronization
  const { currentTime, formatTime, isInitialized: timeServiceInitialized } = useServerTime();

  const loadData = useCallback(async () => {
    try {
      console.log('TabletDashboard: Loading data from API...');
      const promises: Promise<unknown>[] = [
        apiService.getProjects(),
        apiService.getLocations()
      ];
      
      // Load teams if showing all workers
      if (showAllWorkers) {
        promises.push(apiService.getTeams());
      }
      
      const results = await Promise.all(promises);
      const [projectsRes, locationsRes, teamsRes] = results;
      
      console.log('TabletDashboard: Projects loaded:', projectsRes.length);
      console.log('TabletDashboard: Locations loaded:', locationsRes.length);

      setProjects(projectsRes);
      setLocations(locationsRes);
      
      if (showAllWorkers && teamsRes) {
        setAllTeams(teamsRes);
        console.log(`TabletDashboard: Loaded ${teamsRes.length} teams`);
      }

      let allWorkers: UserType[] = [];
      
      if (showAllWorkers) {
        // Fetch all users from all teams
        try {
          const allUsers = await apiService.getAllUsers();
          console.log(`TabletDashboard: Loaded ${allUsers.length} total users from API`);
          
          // Filter to only include workers and supervisors (exclude admin, monitor, table roles)
          allWorkers = allUsers.filter(user => 
            user.role === 'worker' || user.role === 'supervisor'
          );
          
          // Sort workers: supervisors first, then alphabetically by name
          allWorkers.sort((a, b) => {
            if (a.role === 'supervisor' && b.role !== 'supervisor') return -1;
            if (a.role !== 'supervisor' && b.role === 'supervisor') return 1;
            return a.name.localeCompare(b.name, 'ro');
          });
          
          console.log(`TabletDashboard: Showing ${allWorkers.length} workers and supervisors`);
        } catch (error) {
          console.error('Error loading all users for tablet interface:', error);
        }
      } else if (selectedTeams && selectedTeams.length > 0) {
        // Include both team members AND supervisors in the table interface
        const supervisorIds = new Set<number>();
        
        // First, add all team members
        for (const team of selectedTeams) {
          console.log(`TabletDashboard: Processing team ${team.name} (ID: ${team.id}) with supervisor ID: ${team.supervisorId}`);
          allWorkers.push(...team.members);
          supervisorIds.add(team.supervisorId);
        }
        
        console.log(`TabletDashboard: Found ${supervisorIds.size} unique supervisors to load:`, Array.from(supervisorIds));
        
        // Get all users to find supervisors (more efficient than individual calls)
        try {
          const allUsers = await apiService.getAllUsers();
          console.log(`TabletDashboard: Loaded ${allUsers.length} total users from API`);
          
          // Add supervisors that aren't already in the members list
          for (const supervisorId of supervisorIds) {
            const supervisorAlreadyIncluded = allWorkers.some(worker => worker.id === supervisorId);
            if (!supervisorAlreadyIncluded) {
              const supervisor = allUsers.find(user => user.id === supervisorId);
              if (supervisor) {
                allWorkers.push(supervisor);
                console.log(`TabletDashboard: Added supervisor ${supervisor.name} (ID: ${supervisor.id}, Role: ${supervisor.role}) to the tablet interface`);
              } else {
                console.warn(`TabletDashboard: Could not find supervisor with ID ${supervisorId} in users list`);
              }
            } else {
              console.log(`TabletDashboard: Supervisor with ID ${supervisorId} is already included as team member`);
            }
          }
        } catch (error) {
          console.error('Error loading supervisors for tablet interface:', error);
        }
      }
      
      console.log('TabletDashboard: All workers from selected teams (including supervisors):', allWorkers.length);
      console.log('TabletDashboard: Workers list:', allWorkers.map(w => `${w.name} (ID: ${w.id}, Role: ${w.role})`));
      
      const statusPromises = allWorkers.map(async (worker) => {
        try {
          // For supervisors, we need to check both active work entry AND today's entries
          // since supervisors might have complex work session management
          let activeWorkEntry = await apiService.getActiveWorkEntry(worker.id, true);
          
          // If supervisor doesn't have active entry, check today's entries for active sessions
          if (!activeWorkEntry && worker.role === 'supervisor') {
            console.log(`TabletDashboard: Checking today's entries for supervisor ${worker.name}`);
            const todayResponse = await apiService.getWorkEntries(
              new Date().toISOString().split('T')[0], 
              worker.id
            );
            const todayEntries = Array.isArray(todayResponse) ? todayResponse : (todayResponse.data || []);
            
            // Find any active entry from today
            activeWorkEntry = todayEntries.find((entry: { isActive?: boolean; endTime?: string; startTime?: string }) => 
              (entry.isActive === true) || (!entry.endTime && entry.startTime)
            ) || null;
            
            if (activeWorkEntry) {
              console.log(`TabletDashboard: Found active entry for supervisor ${worker.name} from today's data:`, activeWorkEntry.id);
            }
          }
          
          console.log(`TabletDashboard: Active work entry for ${worker.name} (${worker.role}):`, activeWorkEntry?.id || 'none');
          const localOnlineStatus = OnlineStatusService.isUserOnline(worker.id);
          
          // FIXED: Only consider workers as "online" based on database state, not local activation
          // Workers should only show as online if they have real active work entries in the database
          const isOnline = !!activeWorkEntry; // Only database state matters for actual work status
          
          console.log(`TabletDashboard: Status for ${worker.name} - isWorking: ${!!activeWorkEntry}, localOnline: ${localOnlineStatus}, effectiveOnline: ${isOnline}`);
          
          // Determine if worker should be on automatic break (12:00-13:00 Romanian time)
          const isWorking = !!activeWorkEntry;
          const isOnAutomaticBreak = shouldUserBeOnAutomaticBreak(isWorking);
          
          return {
            user: worker,
            activeWorkEntry,
            isWorking,
            isOnline,
            isOnBreak: isOnAutomaticBreak, // Add break status for display consistency
            currentProject: activeWorkEntry ? 
              projectsRes.find(p => p.id === activeWorkEntry.projectId)?.name : undefined,
            currentLocation: activeWorkEntry ? 
              locationsRes.find(l => l.id === activeWorkEntry.locationId)?.name : undefined,
            startTime: activeWorkEntry?.startTime
          };
        } catch (error) {
          console.error(`Error loading status for worker ${worker.name}:`, error);
          return {
            user: worker,
            activeWorkEntry: null,
            isWorking: false,
            isOnline: false,
            isOnBreak: isInAutomaticBreakTime() // Show break if it's break time
          };
        }
      });

      const statuses = await Promise.all(statusPromises);
      setWorkersStatus(statuses);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading table data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTeams, showAllWorkers]);

  // Initialize SignalR connection
  useEffect(() => {
    const initRealTime = async () => {
      try {
        console.log('TabletDashboard: Checking real-time connection state:', realTime.state.connectionState);
        if (realTime.state.connectionState === 'Disconnected') {
          console.log('TabletDashboard: Attempting to connect to real-time service');
          await realTime.connect(user.id.toString(), user.role);
        }
      } catch (error) {
        console.error('TabletDashboard: Error connecting to real-time service:', error);
      }
    };

    initRealTime();
  }, [user.id, user.role, realTime]);

  // Set up real-time event listeners
  useEffect(() => {
    if (!realTime.state.isConnected) return;

    console.log('TabletDashboard: Setting up real-time event listeners');
    
    // Enhanced real-time event handler with immediate data refresh
    const handleRealTimeEvent = async (eventName: string, eventData?: unknown) => {
      console.log(`TabletDashboard: Received ${eventName}, refreshing data immediately...`, eventData);
      
      try {
        // Immediate data refresh to ensure synchronization with supervisor screen
        await loadData();
        console.log(`TabletDashboard: Successfully refreshed data after ${eventName}`);
      } catch (error) {
        console.error(`TabletDashboard: Error refreshing data after ${eventName}:`, error);
      }
    };

    // Listen for work status changes
    const handleWorkEvent = (event: unknown) => {
      handleRealTimeEvent('WorkEvent', event);
    };

    const handleBreakEvent = (event: unknown) => {
      handleRealTimeEvent('BreakEvent', event);
    };

    const handleTeamEvent = (event: unknown) => {
      handleRealTimeEvent('TeamEvent', event);
    };

    // Subscribe to events
    realTime.subscribeToWorkEvents(handleWorkEvent);
    realTime.subscribeToBreakEvents(handleBreakEvent);
    realTime.subscribeToTeamEvents(handleTeamEvent);

    // Also subscribe to specific work started/ended events for more detailed handling
    const unsubscribeWorkStarted = realTime.subscribeToWorkStarted((event) => {
      console.log('TabletDashboard: Work started event received:', event);
      handleRealTimeEvent('WorkStarted', event);
    });

    const unsubscribeWorkEnded = realTime.subscribeToWorkEnded((event) => {
      console.log('TabletDashboard: Work ended event received:', event);
      handleRealTimeEvent('WorkEnded', event);
    });

    return () => {
      console.log('TabletDashboard: Cleaning up real-time event listeners');
      // Clean up specific subscriptions
      if (unsubscribeWorkStarted) unsubscribeWorkStarted();
      if (unsubscribeWorkEnded) unsubscribeWorkEnded();
      // Note: The generic subscriptions don't provide unsubscribe methods
      // This is a limitation that should be addressed in the RealTimeContext
    };
  }, [realTime.state.isConnected, loadData, realTime]);

  useEffect(() => {
    loadData();
    // Reduce polling frequency since we now have real-time updates
    const interval = setInterval(loadData, 60000); // Changed from 30s to 60s
    return () => clearInterval(interval);
  }, [loadData]);

  const handleWorkAction = (worker: UserType, action: 'start' | 'stop') => {
    const workerStatus = workersStatus.find(w => w.user.id === worker.id);
    
    if (action === 'start') {
      // Check if worker is online before allowing work actions
      if (!workerStatus?.isOnline) {
        alert('Lucrătorul trebuie să fie activat înainte de a putea începe lucrul!');
        return;
      }
    } else if (action === 'stop') {
      // Check if worker actually has an active work entry
      if (!workerStatus?.activeWorkEntry || !workerStatus.activeWorkEntry.id) {
        alert('Nu există o sesiune de lucru activă pentru acest muncitor!');
        return;
      }
    }
    
    setConfirmationDialog({
      isOpen: true,
      user: worker,
      action
    });
  };

  const handleActivateWorker = (worker: UserType) => {
    setConfirmationDialog({
      isOpen: true,
      user: worker,
      action: 'activate'
    });
  };

  const recordTabletAction = (workerName: string, action: 'start' | 'stop', details?: { projectName?: string; locationName?: string }) => {
    const tabletAction = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      workerName,
      action,
      tabletName: user.name,
      projectName: details?.projectName,
      locationName: details?.locationName
    };

    const storedActions = localStorage.getItem('tablet-actions');
    let actions = [];
    if (storedActions) {
      try {
        actions = JSON.parse(storedActions);
      } catch (error) {
        console.error('Error parsing existing table actions:', error);
      }
    }
    
    actions.push(tabletAction);
    
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    const filteredActions = actions.filter((a: { timestamp: string }) => new Date(a.timestamp) > oneHourAgo);
    
    localStorage.setItem('tablet-actions', JSON.stringify(filteredActions));
  };

  const handleConfirmWork = async () => {
    const { user: worker, action } = confirmationDialog;
    if (!worker) return;

    // Wait for SignalR connection before allowing work actions
    if (!realTime.state.isConnected) {
      alert('Așteptați conectarea la server înainte de a efectua acțiuni...');
      return;
    }

    try {
      const workerStatus = workersStatus.find(w => w.user.id === worker.id);

      if (action === 'start') {
        if (projects.length === 0 || locations.length === 0) {
          alert('Nu există proiecte sau locații disponibile');
          return;
        }
        
        // Check if work is locked for this worker
        const today = new Date().toISOString().split('T')[0];
        const isLocked = isWorkLockedForUser(worker.id, today);
        if (isLocked) {
          const lockMessage = getWorkLockMessage(worker.id, today);
          alert(`Lucrul este blocat pentru ${worker.name}. ${lockMessage}`);
          return;
        }
        
        // Check if worker already has an active work entry
        if (workerStatus?.isWorking && workerStatus?.activeWorkEntry) {
          alert(`${worker.name} are deja o sesiuni de lucru activă! Nu se poate începe o nouă sesiune.`);
          return;
        }

        // Ensure worker is online before starting work (this will be true if they have active work, but safe to call)
        if (!workerStatus?.isOnline) {
          OnlineStatusService.activateUser(worker.id, worker.name);
        }

        // Get stored default project/location for this table
        const storedDefaults = localStorage.getItem(`tablet-${user.id}-defaults`);
        let defaultProject = projects[0];
        let defaultLocation = locations[0];
        
        if (storedDefaults) {
          try {
            const defaults = JSON.parse(storedDefaults);
            const foundProject = projects.find(p => p.id === defaults.projectId);
            const foundLocation = locations.find(l => l.id === defaults.locationId);
            
            if (foundProject) defaultProject = foundProject;
            if (foundLocation) defaultLocation = foundLocation;
          } catch (error) {
            console.error('Error parsing stored defaults:', error);
          }
        }

        // Use the supervisor API method that triggers SignalR events for cross-screen updates
        // This ensures all screens (supervisor, worker, monitor) get real-time updates
        await apiService.startWorkForUser(worker.id, {
          userId: worker.id,
          projectId: defaultProject.id,
          locationId: defaultLocation.id,
          startTime: getCurrentTimestamp(),
          date: getCurrentDateString()
        });
        
        console.log(`TabletDashboard: Started work for ${worker.name} via table interface`);
        
        // Record the action with project/location details
        recordTabletAction(worker.name, action, {
          projectName: defaultProject.name,
          locationName: defaultLocation.name
        });
      } else if (action === 'stop' && workerStatus?.activeWorkEntry) {
        const workEntryId = workerStatus.activeWorkEntry.id;
        if (!workEntryId) {
          console.error('Work entry ID is undefined:', workerStatus.activeWorkEntry);
          alert('Eroare: Nu s-a găsit ID-ul sesiunii de lucru');
          return;
        }
        
        // Use the supervisor API method that triggers SignalR events for cross-screen updates
        // This ensures all screens (supervisor, worker, monitor) get real-time updates
        await apiService.endWorkForUser(workEntryId, {
          endTime: getCurrentTimestamp(),
          breakMinutes: workerStatus.activeWorkEntry.breakMinutes || 0
        });
        
        console.log(`TabletDashboard: Ended work for ${worker.name} via table interface`);
        
        recordTabletAction(worker.name, action);
      } else if (action === 'activate') {
        const success = OnlineStatusService.activateUser(worker.id, worker.name);
        if (!success) {
          alert('Eroare la activarea lucrătorului');
          return;
        }
      }
      setConfirmationDialog({ isOpen: false, user: null, action: 'start' });
      
      // Immediate data refresh to show changes quickly
      await loadData();
      
      // Small delay to ensure SignalR events have been processed by other screens
      setTimeout(async () => {
        try {
          await loadData();
          console.log('TabletDashboard: Performed follow-up data refresh for cross-screen sync');
        } catch (refreshError) {
          console.error('TabletDashboard: Error in follow-up refresh:', refreshError);
        }
      }, 500);
      
    } catch (error) {
      console.error('Error updating work status:', error);
      alert(`Eroare la actualizarea stării lucrului: ${error instanceof Error ? error.message : 'Eroare necunoscută'}`);
    }
  };

  const handleCancelConfirmation = () => {
    setConfirmationDialog({ isOpen: false, user: null, action: 'start' });
  };

  const formatWorkTime = (timeString?: string) => {
    if (!timeString) return '';
    const time = new Date(timeString);
    return time.toLocaleTimeString('ro-RO', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDuration = (startTime?: string) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'supervisor':
        return <Shield className="w-4 h-4" />;
      case 'monitor':
        return <Eye className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'supervisor':
        return 'bg-purple-100 text-purple-800';
      case 'monitor':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă datele...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Masă de Control - {user.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-gray-600">
                {showAllWorkers ? 'Monitorizare toți lucrătorii' : 'Monitorizare echipe'} - {formatTime('date')}
                {!timeServiceInitialized && <span className="text-orange-600 ml-2">(sincronizare...)</span>}
              </p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  realTime.state.isConnected ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className={`text-xs font-medium ${
                  realTime.state.isConnected ? 'text-green-600' : 'text-red-600'
                }`}>
                  {realTime.state.isConnected ? 'Actualizări în timp real' : 'Doar polling'}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                Ultima actualizare: {lastUpdated.toLocaleTimeString('ro-RO')}
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={loadData}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reîmprospătează
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="flex items-center gap-2 text-red-600 border-red-300"
            >
              <LogOut className="w-4 h-4" />
              Deconectare
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Lucrători</p>
                  <p className="text-2xl font-bold">{workersStatus.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Online</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {workersStatus.filter(w => w.isOnline).length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">La Lucru</p>
                  <p className="text-2xl font-bold text-green-600">
                    {workersStatus.filter(w => w.isWorking).length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{showAllWorkers ? 'Supervisori' : 'Echipe Active'}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {showAllWorkers 
                      ? workersStatus.filter(w => w.user.role === 'supervisor').length 
                      : (selectedTeams?.length || 0)}
                  </p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Automatic Break Status */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Coffee className={`w-5 h-5 ${isInAutomaticBreakTime() ? 'text-orange-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {isInAutomaticBreakTime() ? 'Pauza automată activă' : 'Pauza automată inactivă'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Pauza automată: zilnic 12:00-13:00 (ora României)
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">
                  În pauză: {workersStatus.filter(w => w.isOnBreak).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {showAllWorkers ? (
            // Show all workers grouped by teams
            <>
              {/* Workers without teams */}
              {(() => {
                const workersWithoutTeam = workersStatus.filter(w => !w.user.teamId);
                if (workersWithoutTeam.length > 0) {
                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          Lucrători fără echipă
                          <Badge variant="outline" className="ml-auto">
                            {workersWithoutTeam.length} persoane
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4">
                          {workersWithoutTeam.map((workerStatus) => (
                            <WorkerCard
                              key={workerStatus.user.id}
                              workerStatus={workerStatus}
                              handleActivateWorker={handleActivateWorker}
                              handleWorkAction={handleWorkAction}
                              realTimeConnected={realTime.state.isConnected}
                              getRoleIcon={getRoleIcon}
                              getRoleColor={getRoleColor}
                              formatWorkTime={formatWorkTime}
                              formatDuration={formatDuration}
                            />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                }
                return null;
              })()}
              
              {/* Workers grouped by teams */}
              {allTeams.map((team) => {
                const teamWorkers = workersStatus.filter(w => 
                  w.user.teamId === team.id || w.user.id === team.supervisorId
                );
                
                if (teamWorkers.length === 0) return null;
                
                return (
                  <Card key={team.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        {team.name}
                        <Badge variant="outline" className="ml-auto">
                          {teamWorkers.length} membri
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4">
                        {teamWorkers.map((workerStatus) => (
                          <WorkerCard
                            key={workerStatus.user.id}
                            workerStatus={workerStatus}
                            handleActivateWorker={handleActivateWorker}
                            handleWorkAction={handleWorkAction}
                            realTimeConnected={realTime.state.isConnected}
                            getRoleIcon={getRoleIcon}
                            getRoleColor={getRoleColor}
                            formatWorkTime={formatWorkTime}
                            formatDuration={formatDuration}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Show message if no workers */}
              {workersStatus.length === 0 && (
                <Card>
                  <CardContent className="p-8">
                    <p className="text-center text-gray-500">
                      Nu sunt lucrători disponibili
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            // Show workers grouped by teams
            selectedTeams?.map((team) => {
              // Include both team members AND the supervisor for this team
              const teamWorkers = workersStatus.filter(w => 
                w.user.teamId === team.id || w.user.id === team.supervisorId
              );
              
              console.log(`TabletDashboard: Team ${team.name} - showing ${teamWorkers.length} workers:`, 
                teamWorkers.map(w => `${w.user.name} (ID: ${w.user.id}, Role: ${w.user.role}, TeamId: ${w.user.teamId})`));
              
              return (
                <Card key={team.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {team.name}
                      <Badge variant="outline" className="ml-auto">
                        {teamWorkers.length} membri
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {teamWorkers.map((workerStatus) => (
                        <WorkerCard
                          key={workerStatus.user.id}
                          workerStatus={workerStatus}
                          handleActivateWorker={handleActivateWorker}
                          handleWorkAction={handleWorkAction}
                          realTimeConnected={realTime.state.isConnected}
                          getRoleIcon={getRoleIcon}
                          getRoleColor={getRoleColor}
                          formatWorkTime={formatWorkTime}
                          formatDuration={formatDuration}
                        />
                      ))}
                      {teamWorkers.length === 0 && (
                        <p className="text-center text-gray-500 py-4">
                          Nu sunt membri în această echipă
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          Ultima actualizare: {lastUpdated.toLocaleTimeString('ro-RO')}
        </div>
      </div>

      <WorkConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        user={confirmationDialog.user}
        action={confirmationDialog.action}
        onConfirm={handleConfirmWork}
        onCancel={handleCancelConfirmation}
      />
    </div>
  );
};

export default TabletDashboard;